/* The guide panel, reproduced from the reference.
 *
 * **Every visual decision here is the reference's**, transcribed into `lq-guide__*` rules in
 * `system.css` rather than the inline styles the reference uses — because `tokens.test.ts` fails the
 * build for a raw length in a `style` prop, and for the reason that sweep records: an inline style
 * beats every stylesheet, so it also defeats the coarse-pointer rule that raises controls to a 44px
 * target. The rendered result is the same; where it is enforceable is not.
 *
 * Three tabs, and the two that are not `Start here` carry the weight:
 *
 * * **`This page` follows the reader.** It reads the current route on every render, so navigating
 *   with the panel open updates it — which is what makes it worth having rather than a static
 *   index.
 * * **`All topics` searches title and description**, because somebody looking for "credentials" is
 *   describing what they want rather than naming `Connections`.
 */

import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { START_STEPS, groupedTopics, topicById, topicForPath } from '../model/content';
import {
  doneCount,
  readProgress,
  toggled,
  writeProgress,
  type GuideProgress,
} from '../model/progress';

type GuideTab = 'start' | 'page' | 'topics';

const TABS: readonly { readonly id: GuideTab; readonly labelKey: string }[] = [
  { id: 'start', labelKey: 'guide.tab.start' },
  { id: 'page', labelKey: 'guide.tab.page' },
  { id: 'topics', labelKey: 'guide.tab.topics' },
];

export interface GuidePanelProps {
  readonly onClose: () => void;
}

export function GuidePanel({ onClose }: GuidePanelProps): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState<GuideTab>('start');
  const [query, setQuery] = useState('');
  /* Read once on mount rather than on every render: `localStorage` is synchronous and this is a
     panel that re-renders on every keystroke in its own search box. */
  const [progress, setProgress] = useState<GuideProgress>(() => readProgress());

  const startIds = useMemo(() => START_STEPS.map((step) => step.id), []);
  const done = doneCount(progress, startIds);
  const allDone = done === START_STEPS.length;
  const percent = Math.round((done / START_STEPS.length) * 100);

  const current = topicForPath(location.pathname);
  const groups = useMemo(() => groupedTopics(query, t), [query, t]);

  const apply = (next: GuideProgress): void => {
    setProgress(next);
    writeProgress(next);
  };

  /* **Opening a screen also switches to `This page`**, which is the reference's behaviour and is the
     half that makes the checklist useful: a reader who clicks "Open Connections" wants the panel to
     keep explaining, not to sit on a list they have just left. */
  const open = (path: string): void => {
    navigate(path);
    setTab('page');
  };

  return (
    <aside className="lq-guide" aria-label={t('guide.panel.label')}>
      <div className="lq-guide__head">
        <div className="lq-guide__headRow">
          <div className="lq-guide__heading">
            <div className="lq-guide__eyebrow">{t('guide.eyebrow')}</div>
            <h2 className="lq-guide__title">{t('guide.title')}</h2>
          </div>
          <button
            type="button"
            className="lq-guide__close"
            onClick={onClose}
            aria-label={t('guide.close')}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="lq-guide__tabs" role="tablist" aria-label={t('guide.tabs.label')}>
          {TABS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              role="tab"
              aria-selected={tab === entry.id}
              className="lq-guide__tab"
              data-active={tab === entry.id ? 'yes' : 'no'}
              onClick={() => {
                setTab(entry.id);
              }}
            >
              {t(entry.labelKey)}
            </button>
          ))}
        </div>
      </div>

      <div className="lq-guide__body">
        {tab === 'start' ? (
          <>
            <p className="lq-guide__lead">{t('guide.start.lead')}</p>
            <div className="lq-guide__progress">
              {/* The bar is decorative — the figure beside it says the same thing in words, which is
                  what a screen reader gets. */}
              <div className="lq-guide__track" aria-hidden="true">
                <div
                  className="lq-guide__fill"
                  style={{ width: `${String(percent)}%` }}
                />
              </div>
              <span className="lq-guide__count">
                {t('guide.start.progress', { done, total: START_STEPS.length })}
              </span>
            </div>

            <ol className="lq-guide__steps">
              {START_STEPS.map((step, index) => {
                const isDone = progress[step.id] === true;
                const topic = topicById(step.topicId);
                const toggleLabel = isDone
                  ? t('guide.start.markNotDone')
                  : t('guide.start.markDone');
                return (
                  <li key={step.id} className="lq-guide__step">
                    <button
                      type="button"
                      className="lq-guide__tick"
                      data-done={isDone ? 'yes' : 'no'}
                      onClick={() => {
                        apply(toggled(progress, step.id));
                      }}
                      aria-pressed={isDone}
                      aria-label={toggleLabel}
                      title={toggleLabel}
                    >
                      {isDone ? (
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                          focusable="false"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        String(index + 1)
                      )}
                    </button>
                    <div>
                      <div
                        className="lq-guide__stepTitle"
                        data-done={isDone ? 'yes' : 'no'}
                      >
                        {t(step.titleKey)}
                      </div>
                      <div className="lq-guide__stepBody">{t(step.bodyKey)}</div>
                      <div className="lq-guide__stepActions">
                        {topic !== undefined ? (
                          <button
                            type="button"
                            className="lq-guide__link"
                            onClick={() => {
                              open(topic.path);
                            }}
                          >
                            {t('guide.start.open', { screen: t(topic.titleKey) })}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="lq-guide__link lq-guide__link--quiet"
                          onClick={() => {
                            apply(toggled(progress, step.id));
                          }}
                        >
                          {toggleLabel}
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>

            {allDone ? (
              <div className="lq-guide__done" role="status">
                {t('guide.start.allDone')}{' '}
                <button
                  type="button"
                  className="lq-guide__link"
                  onClick={() => {
                    apply({});
                  }}
                >
                  {t('guide.start.startOver')}
                </button>
              </div>
            ) : null}

            <div className="lq-guide__note">
              <strong className="lq-guide__noteLead">{t('guide.start.rolesLead')}</strong>{' '}
              {t('guide.start.rolesBody')}
            </div>
          </>
        ) : null}

        {tab === 'page' ? (
          <>
            <div className="lq-guide__group">{t(current.groupKey)}</div>
            <h3 className="lq-guide__pageTitle">{t(current.titleKey)}</h3>
            <p className="lq-guide__what">{t(current.whatKey)}</p>
            <div className="lq-guide__doLabel">{t('guide.page.whatToDo')}</div>
            <div className="lq-guide__do">
              {current.stepKeys.map((key, index) => (
                <div key={key} className="lq-guide__doRow">
                  <span className="lq-guide__doNum">{index + 1}</span>
                  <span>{t(key)}</span>
                </div>
              ))}
            </div>
            <p className="lq-guide__follows">{t('guide.page.follows')}</p>
          </>
        ) : null}

        {tab === 'topics' ? (
          <>
            <div className="lq-guide__search">
              <svg
                className="lq-guide__searchIcon"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
                focusable="false"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              <input
                className="lq-guide__searchInput"
                type="search"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                }}
                placeholder={t('guide.topics.placeholder')}
                aria-label={t('guide.topics.search')}
              />
            </div>

            {groups.length === 0 ? (
              <div className="lq-guide__noMatch">{t('guide.topics.noMatch')}</div>
            ) : null}

            {groups.map((group) => (
              <div key={group.groupKey} className="lq-guide__topicGroup">
                <div className="lq-guide__groupName">{t(group.groupKey)}</div>
                {group.topics.map((topic) => (
                  <button
                    key={topic.id}
                    type="button"
                    className="lq-guide__topic"
                    onClick={() => {
                      open(topic.path);
                    }}
                  >
                    <div className="lq-guide__topicLabel">
                      {t(topic.titleKey)}
                      {topic.id === current.id ? (
                        <span className="lq-guide__here">{t('guide.topics.here')}</span>
                      ) : null}
                    </div>
                    <div className="lq-guide__topicWhat">{t(topic.whatKey)}</div>
                  </button>
                ))}
              </div>
            ))}
          </>
        ) : null}
      </div>

      <div className="lq-guide__foot">
        {t('guide.foot.press')} <span className="lq-guide__kbd">?</span>{' '}
        {t('guide.foot.toggle')} <span className="lq-guide__kbd">Esc</span>{' '}
        {t('guide.foot.close')}
      </div>
    </aside>
  );
}
